import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Menu, X, Sun, Moon, Globe, 
  Smartphone, Store, Bike, Clock, 
  Shield, MapPin, Star,
  Apple, Play,
  CheckCircle2
} from 'lucide-react';
import { useSettingsStore } from '@/stores/settingsStore';

// Translations for landing page
const landingTranslations = {
  ar: {
    nav: {
      home: 'الرئيسية',
      features: 'المميزات',
      howItWorks: 'كيف يعمل',
      download: 'التحميل',
      login: 'تسجيل الدخول',
    },
    hero: {
      badge: '🚀 منصة التوصيل الأولى في مصر',
      title: 'اطلب قهوتك المفضلة',
      titleHighlight: 'بضغطة زر',
      subtitle: 'أليستا تربطك بأفضل المقاهي في منطقتك مع توصيل سريع وموثوق. استمتع بتجربة طلب فريدة.',
      orderNow: 'اطلب الآن',
      joinAsDriver: 'انضم كسائق',
      stats: {
        orders: '+50K',
        ordersLabel: 'طلب تم توصيله',
        cafes: '+200',
        cafesLabel: 'مقهى شريك',
        drivers: '+500',
        driversLabel: 'سائق نشط',
        rating: '4.9',
        ratingLabel: 'تقييم العملاء',
      },
    },
    features: {
      title: 'لماذا أليستا؟',
      subtitle: 'نقدم لك تجربة توصيل لا مثيل لها',
      items: [
        {
          icon: 'clock',
          title: 'توصيل سريع',
          description: 'نضمن وصول طلبك في أقل من 30 دقيقة أو الطلب مجاني',
        },
        {
          icon: 'shield',
          title: 'دفع آمن',
          description: 'طرق دفع متعددة وآمنة تشمل البطاقات والمحافظ الإلكترونية',
        },
        {
          icon: 'mappin',
          title: 'تتبع مباشر',
          description: 'تابع طلبك لحظة بلحظة من المقهى حتى باب منزلك',
        },
        {
          icon: 'star',
          title: 'أفضل المقاهي',
          description: 'مقاهي مختارة بعناية تضمن جودة القهوة والخدمة',
        },
      ],
    },
    howItWorks: {
      title: 'كيف يعمل التطبيق؟',
      subtitle: '3 خطوات بسيطة للحصول على قهوتك',
      steps: [
        {
          number: '01',
          title: 'اختر مقهاك المفضل',
          description: 'تصفح قائمة المقاهي القريبة منك واختر ما يناسبك',
        },
        {
          number: '02',
          title: 'أضف طلبك',
          description: 'اختر المشروبات والوجبات الخفيفة وخصصها حسب رغبتك',
        },
        {
          number: '03',
          title: 'استلم بسرعة',
          description: 'سائقنا في طريقه إليك، تابع الطلب لحظة بلحظة',
        },
      ],
    },
    download: {
      title: 'حمّل التطبيق الآن',
      subtitle: 'متوفر على جميع المنصات',
      customer: 'تطبيق العملاء',
      driver: 'تطبيق السائقين',
      cafe: 'تطبيق المقاهي',
    },
    footer: {
      description: 'منصة التوصيل الأولى للمقاهي في مصر',
      links: 'روابط سريعة',
      support: 'الدعم',
      contact: 'تواصل معنا',
      privacy: 'سياسة الخصوصية',
      terms: 'الشروط والأحكام',
      faq: 'الأسئلة الشائعة',
      copyright: '© 2026 أليستا. جميع الحقوق محفوظة.',
    },
  },
  en: {
    nav: {
      home: 'Home',
      features: 'Features',
      howItWorks: 'How it Works',
      download: 'Download',
      login: 'Login',
    },
    hero: {
      badge: '🚀 Egypt\'s #1 Delivery Platform',
      title: 'Order Your Favorite Coffee',
      titleHighlight: 'With One Tap',
      subtitle: 'Allista connects you with the best cafes in your area with fast and reliable delivery. Enjoy a unique ordering experience.',
      orderNow: 'Order Now',
      joinAsDriver: 'Join as Driver',
      stats: {
        orders: '+50K',
        ordersLabel: 'Orders Delivered',
        cafes: '+200',
        cafesLabel: 'Partner Cafes',
        drivers: '+500',
        driversLabel: 'Active Drivers',
        rating: '4.9',
        ratingLabel: 'Customer Rating',
      },
    },
    features: {
      title: 'Why Allista?',
      subtitle: 'We offer you an unparalleled delivery experience',
      items: [
        {
          icon: 'clock',
          title: 'Fast Delivery',
          description: 'We guarantee your order arrives in under 30 minutes or it\'s free',
        },
        {
          icon: 'shield',
          title: 'Secure Payment',
          description: 'Multiple secure payment methods including cards and e-wallets',
        },
        {
          icon: 'mappin',
          title: 'Live Tracking',
          description: 'Track your order in real-time from the cafe to your doorstep',
        },
        {
          icon: 'star',
          title: 'Top Cafes',
          description: 'Carefully selected cafes ensuring quality coffee and service',
        },
      ],
    },
    howItWorks: {
      title: 'How Does It Work?',
      subtitle: '3 simple steps to get your coffee',
      steps: [
        {
          number: '01',
          title: 'Choose Your Cafe',
          description: 'Browse nearby cafes and pick your favorite',
        },
        {
          number: '02',
          title: 'Add Your Order',
          description: 'Select drinks and snacks, customize to your liking',
        },
        {
          number: '03',
          title: 'Quick Delivery',
          description: 'Our driver is on the way, track in real-time',
        },
      ],
    },
    download: {
      title: 'Download the App Now',
      subtitle: 'Available on all platforms',
      customer: 'Customer App',
      driver: 'Driver App',
      cafe: 'Cafe App',
    },
    footer: {
      description: 'Egypt\'s #1 Cafe Delivery Platform',
      links: 'Quick Links',
      support: 'Support',
      contact: 'Contact Us',
      privacy: 'Privacy Policy',
      terms: 'Terms & Conditions',
      faq: 'FAQ',
      copyright: '© 2026 Allista. All rights reserved.',
    },
  },
};

const iconMap: Record<string, React.ElementType> = {
  clock: Clock,
  shield: Shield,
  mappin: MapPin,
  star: Star,
};

export default function LandingPage() {
  const { theme, toggleTheme, language, toggleLanguage } = useSettingsStore();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  
  const t = landingTranslations[language];

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className={`min-h-screen ${theme === 'dark' ? 'dark bg-slate-900' : 'bg-white'}`}>
      {/* Navbar */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled 
          ? 'bg-white/90 dark:bg-slate-900/90 backdrop-blur-lg shadow-lg' 
          : 'bg-transparent'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 lg:h-20">
            {/* Logo */}
            <div className="flex items-center gap-2">
              <span className="text-2xl">🚀</span>
              <span className="text-xl font-bold text-slate-800 dark:text-white">أليستا</span>
            </div>

            {/* Desktop Nav */}
            <div className="hidden lg:flex items-center gap-8">
              <a href="#home" className="text-slate-600 dark:text-slate-300 hover:text-primary-600 dark:hover:text-primary-400 font-medium transition-colors">
                {t.nav.home}
              </a>
              <a href="#features" className="text-slate-600 dark:text-slate-300 hover:text-primary-600 dark:hover:text-primary-400 font-medium transition-colors">
                {t.nav.features}
              </a>
              <a href="#how-it-works" className="text-slate-600 dark:text-slate-300 hover:text-primary-600 dark:hover:text-primary-400 font-medium transition-colors">
                {t.nav.howItWorks}
              </a>
              <a href="#download" className="text-slate-600 dark:text-slate-300 hover:text-primary-600 dark:hover:text-primary-400 font-medium transition-colors">
                {t.nav.download}
              </a>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-3">
              <button
                onClick={toggleLanguage}
                className="p-2 rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
              >
                <Globe size={20} />
              </button>
              <button
                onClick={toggleTheme}
                className="p-2 rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
              >
                {theme === 'light' ? <Moon size={20} /> : <Sun size={20} />}
              </button>
              <Link
                to="/login"
                className="hidden sm:inline-flex px-5 py-2.5 bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white font-semibold rounded-xl transition-all shadow-lg shadow-primary-500/25 hover:shadow-primary-500/40"
              >
                {t.nav.login}
              </Link>
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="lg:hidden p-2 rounded-xl bg-slate-100 dark:bg-slate-800"
              >
                {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden bg-white dark:bg-slate-900 border-t border-slate-100 dark:border-slate-800 shadow-lg">
            <div className="p-4 space-y-3">
              <a href="#home" className="block py-2 text-slate-600 dark:text-slate-300 font-medium">{t.nav.home}</a>
              <a href="#features" className="block py-2 text-slate-600 dark:text-slate-300 font-medium">{t.nav.features}</a>
              <a href="#how-it-works" className="block py-2 text-slate-600 dark:text-slate-300 font-medium">{t.nav.howItWorks}</a>
              <a href="#download" className="block py-2 text-slate-600 dark:text-slate-300 font-medium">{t.nav.download}</a>
              <Link to="/login" className="block w-full py-3 bg-primary-500 text-white text-center font-semibold rounded-xl">
                {t.nav.login}
              </Link>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center pt-20 overflow-hidden">
        {/* Background Gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary-50 via-white to-emerald-50 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800" />
        
        {/* Decorative Circles */}
        <div className="absolute top-20 right-10 w-72 h-72 bg-primary-200/30 dark:bg-primary-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-20 left-10 w-96 h-96 bg-emerald-200/30 dark:bg-emerald-500/10 rounded-full blur-3xl" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Content */}
            <div className="text-center lg:text-start">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary-100 dark:bg-primary-900/30 rounded-full text-primary-700 dark:text-primary-400 text-sm font-medium mb-6">
                {t.hero.badge}
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-800 dark:text-white leading-tight mb-6">
                {t.hero.title}
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-primary-500 to-emerald-500">
                  {t.hero.titleHighlight}
                </span>
              </h1>
              
              <p className="text-lg text-slate-600 dark:text-slate-400 mb-8 max-w-xl mx-auto lg:mx-0">
                {t.hero.subtitle}
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <button className="px-8 py-4 bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white font-bold rounded-2xl transition-all shadow-xl shadow-primary-500/25 hover:shadow-primary-500/40 flex items-center justify-center gap-2">
                  <Smartphone size={20} />
                  {t.hero.orderNow}
                </button>
                <button className="px-8 py-4 bg-white dark:bg-slate-800 text-slate-800 dark:text-white font-bold rounded-2xl border-2 border-slate-200 dark:border-slate-700 hover:border-primary-500 dark:hover:border-primary-500 transition-all flex items-center justify-center gap-2">
                  <Bike size={20} />
                  {t.hero.joinAsDriver}
                </button>
              </div>
              
              {/* Stats */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 mt-12 pt-8 border-t border-slate-200 dark:border-slate-700">
                <div>
                  <p className="text-2xl sm:text-3xl font-extrabold text-primary-600 dark:text-primary-400">{t.hero.stats.orders}</p>
                  <p className="text-sm text-slate-500 dark:text-slate-400">{t.hero.stats.ordersLabel}</p>
                </div>
                <div>
                  <p className="text-2xl sm:text-3xl font-extrabold text-primary-600 dark:text-primary-400">{t.hero.stats.cafes}</p>
                  <p className="text-sm text-slate-500 dark:text-slate-400">{t.hero.stats.cafesLabel}</p>
                </div>
                <div>
                  <p className="text-2xl sm:text-3xl font-extrabold text-primary-600 dark:text-primary-400">{t.hero.stats.drivers}</p>
                  <p className="text-sm text-slate-500 dark:text-slate-400">{t.hero.stats.driversLabel}</p>
                </div>
                <div>
                  <p className="text-2xl sm:text-3xl font-extrabold text-primary-600 dark:text-primary-400">{t.hero.stats.rating}</p>
                  <p className="text-sm text-slate-500 dark:text-slate-400">{t.hero.stats.ratingLabel}</p>
                </div>
              </div>
            </div>
            
            {/* Hero Image / Phone Mockup */}
            <div className="relative hidden lg:flex justify-center items-center">
              <div className="relative">
                {/* Phone mockup */}
                <div className="w-72 h-[580px] bg-slate-900 rounded-[3rem] p-3 shadow-2xl">
                  <div className="w-full h-full bg-gradient-to-br from-primary-500 to-emerald-500 rounded-[2.5rem] flex flex-col items-center justify-center text-white">
                    <span className="text-6xl mb-4">☕</span>
                    <span className="text-2xl font-bold">أليستا</span>
                    <span className="text-sm opacity-80 mt-2">قهوتك في دقائق</span>
                  </div>
                </div>
                
                {/* Floating cards */}
                <div className="absolute -left-20 top-20 bg-white dark:bg-slate-800 p-4 rounded-2xl shadow-xl animate-bounce" style={{ animationDuration: '3s' }}>
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-emerald-100 dark:bg-emerald-900/30 rounded-xl flex items-center justify-center">
                      <CheckCircle2 className="text-emerald-500" size={24} />
                    </div>
                    <div>
                      <p className="font-bold text-slate-800 dark:text-white text-sm">تم التوصيل!</p>
                      <p className="text-xs text-slate-500">منذ دقيقتين</p>
                    </div>
                  </div>
                </div>
                
                <div className="absolute -right-16 bottom-32 bg-white dark:bg-slate-800 p-4 rounded-2xl shadow-xl animate-bounce" style={{ animationDuration: '4s', animationDelay: '1s' }}>
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-amber-100 dark:bg-amber-900/30 rounded-xl flex items-center justify-center">
                      <Star className="text-amber-500" size={24} />
                    </div>
                    <div>
                      <p className="font-bold text-slate-800 dark:text-white text-sm">تقييم 5 نجوم</p>
                      <p className="text-xs text-slate-500">شكراً لك!</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 lg:py-32 bg-slate-50 dark:bg-slate-800/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-800 dark:text-white mb-4">
              {t.features.title}
            </h2>
            <p className="text-lg text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
              {t.features.subtitle}
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {t.features.items.map((item, index) => {
              const Icon = iconMap[item.icon];
              return (
                <div 
                  key={index}
                  className="group p-6 lg:p-8 bg-white dark:bg-slate-800 rounded-3xl shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-2"
                >
                  <div className="w-14 h-14 bg-gradient-to-br from-primary-500 to-emerald-500 rounded-2xl flex items-center justify-center text-white mb-6 group-hover:scale-110 transition-transform">
                    <Icon size={28} />
                  </div>
                  <h3 className="text-xl font-bold text-slate-800 dark:text-white mb-3">
                    {item.title}
                  </h3>
                  <p className="text-slate-600 dark:text-slate-400">
                    {item.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How it Works */}
      <section id="how-it-works" className="py-20 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-800 dark:text-white mb-4">
              {t.howItWorks.title}
            </h2>
            <p className="text-lg text-slate-600 dark:text-slate-400">
              {t.howItWorks.subtitle}
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {t.howItWorks.steps.map((step, index) => (
              <div key={index} className="relative text-center group">
                <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-primary-500 to-emerald-500 text-white text-2xl font-extrabold rounded-3xl mb-6 shadow-lg shadow-primary-500/25 group-hover:scale-110 transition-transform">
                  {step.number}
                </div>
                <h3 className="text-xl font-bold text-slate-800 dark:text-white mb-3">
                  {step.title}
                </h3>
                <p className="text-slate-600 dark:text-slate-400">
                  {step.description}
                </p>
                
                {/* Connector line */}
                {index < 2 && (
                  <div className="hidden md:block absolute top-10 left-full w-full h-0.5 bg-gradient-to-r from-primary-300 to-transparent dark:from-primary-700" style={{ width: 'calc(100% - 5rem)', marginLeft: '2.5rem' }} />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Download Section */}
      <section id="download" className="py-20 lg:py-32 bg-gradient-to-br from-primary-600 to-emerald-600 relative overflow-hidden">
        {/* Decorative */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-extrabold text-white mb-4">
            {t.download.title}
          </h2>
          <p className="text-lg text-white/80 mb-12">
            {t.download.subtitle}
          </p>
          
          <div className="grid sm:grid-cols-3 gap-6 max-w-3xl mx-auto">
            {/* Customer App */}
            <div className="bg-white/10 backdrop-blur-sm p-6 rounded-3xl border border-white/20 hover:bg-white/20 transition-colors">
              <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Smartphone className="text-primary-600" size={32} />
              </div>
              <h3 className="text-lg font-bold text-white mb-4">{t.download.customer}</h3>
              <div className="flex flex-col gap-2">
                <button className="flex items-center justify-center gap-2 px-4 py-2 bg-slate-900 text-white rounded-xl text-sm font-medium hover:bg-slate-800 transition-colors">
                  <Apple size={18} /> App Store
                </button>
                <button className="flex items-center justify-center gap-2 px-4 py-2 bg-slate-900 text-white rounded-xl text-sm font-medium hover:bg-slate-800 transition-colors">
                  <Play size={18} /> Google Play
                </button>
              </div>
            </div>
            
            {/* Driver App */}
            <div className="bg-white/10 backdrop-blur-sm p-6 rounded-3xl border border-white/20 hover:bg-white/20 transition-colors">
              <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Bike className="text-primary-600" size={32} />
              </div>
              <h3 className="text-lg font-bold text-white mb-4">{t.download.driver}</h3>
              <div className="flex flex-col gap-2">
                <button className="flex items-center justify-center gap-2 px-4 py-2 bg-slate-900 text-white rounded-xl text-sm font-medium hover:bg-slate-800 transition-colors">
                  <Apple size={18} /> App Store
                </button>
                <button className="flex items-center justify-center gap-2 px-4 py-2 bg-slate-900 text-white rounded-xl text-sm font-medium hover:bg-slate-800 transition-colors">
                  <Play size={18} /> Google Play
                </button>
              </div>
            </div>
            
            {/* Cafe App */}
            <div className="bg-white/10 backdrop-blur-sm p-6 rounded-3xl border border-white/20 hover:bg-white/20 transition-colors">
              <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Store className="text-primary-600" size={32} />
              </div>
              <h3 className="text-lg font-bold text-white mb-4">{t.download.cafe}</h3>
              <div className="flex flex-col gap-2">
                <button className="flex items-center justify-center gap-2 px-4 py-2 bg-slate-900 text-white rounded-xl text-sm font-medium hover:bg-slate-800 transition-colors">
                  <Apple size={18} /> App Store
                </button>
                <button className="flex items-center justify-center gap-2 px-4 py-2 bg-slate-900 text-white rounded-xl text-sm font-medium hover:bg-slate-800 transition-colors">
                  <Play size={18} /> Google Play
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8 mb-12">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <span className="text-2xl">🚀</span>
                <span className="text-xl font-bold">أليستا</span>
              </div>
              <p className="text-slate-400">{t.footer.description}</p>
            </div>
            
            <div>
              <h4 className="font-bold mb-4">{t.footer.links}</h4>
              <ul className="space-y-2 text-slate-400">
                <li><a href="#home" className="hover:text-white transition-colors">{t.nav.home}</a></li>
                <li><a href="#features" className="hover:text-white transition-colors">{t.nav.features}</a></li>
                <li><a href="#how-it-works" className="hover:text-white transition-colors">{t.nav.howItWorks}</a></li>
                <li><a href="#download" className="hover:text-white transition-colors">{t.nav.download}</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-bold mb-4">{t.footer.support}</h4>
              <ul className="space-y-2 text-slate-400">
                <li><a href="#" className="hover:text-white transition-colors">{t.footer.faq}</a></li>
                <li><a href="#" className="hover:text-white transition-colors">{t.footer.contact}</a></li>
                <li><a href="#" className="hover:text-white transition-colors">{t.footer.privacy}</a></li>
                <li><a href="#" className="hover:text-white transition-colors">{t.footer.terms}</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-bold mb-4">{t.footer.contact}</h4>
              <ul className="space-y-2 text-slate-400">
                <li>info@allista-eg.com</li>
                <li>+20 100 123 4567</li>
                <li>القاهرة، مصر</li>
              </ul>
            </div>
          </div>
          
          <div className="pt-8 border-t border-slate-800 text-center text-slate-500">
            {t.footer.copyright}
          </div>
        </div>
      </footer>
    </div>
  );
}
